/**
 * Shared pieces of the eleven drawn state frames.
 *
 * Everything here is read off `design/Oprappo P0 States.dc.html`. Nothing is designed.
 * A piece lives here only when two or more frames draw it identically; where a frame
 * differs, the frame writes its own markup and this file is not used.
 *
 * WHAT IS NOT HERE, AND WHY
 *
 * `@oprappo/ui` already exports `EmptyState`, `Skeleton`, `Alert` and `FailureState`,
 * and none of them is forked here. They are used where the States frame draws the same
 * block the design system drew. Where the States frame draws something the component
 * library has no form for — the warning panel, the per-row stale chip, the offline
 * wave-with-slash glyph — it is transcribed here and reported as a `packages/ui` gap
 * rather than added to a package this work package does not own (docs/10 § rule 1).
 *
 * `box-content`, WHEREVER A SIZED BOX ALSO HAS A BORDER
 *
 * The design canvas is content-box everywhere outside a frame box, and `styles.css` sets
 * `box-sizing:border-box` on everything. So `width:36px;border:1px` is 38px drawn and
 * 36px transcribed, and the whole row after it moves. That is worth two pixels of care:
 * the eleven frames are dense in bordered boxes — search fields, icon squares, timeline
 * dots, outline buttons — and each one shifts everything below it, which is how a frame
 * that is otherwise exact arrives at a 5% diff with nothing wrong in it. Every sized
 * bordered box in this package is therefore `box-content`, matching `routes/offering`
 * and `routes/activity`, which reached the same conclusion the same way.
 *
 * The same reset does NOT clear the UA's `padding:1px 6px` on `<button>`. A drawn
 * `<div style="height:50px;border:1.5px">` transcribed onto a button is 2px taller than
 * drawn unless it also carries `p-0`. The design draws these as divs; they are buttons
 * here because they are pressable, and `p-0` is what keeps that from costing pixels.
 */
import type { ReactNode } from 'react';
import { ariaLabel } from '@oprappo/tokens';
import { FRAME_BOX } from '../responsive/frame.js';

/**
 * The frame box every phone state is drawn in:
 *
 *     <div style="background:#F4F0E7;border:1px solid #E2DED5;height:680px;
 *                 display:flex;flex-direction:column;box-sizing:border-box">
 *
 * with `width:360px` on the wrapper. § ১০ · অনুমতি নেই is the one exception — it is
 * drawn `background:#0A2E37;border:1px solid #0A2E37` — so the surface is a prop.
 *
 * `data-state-frame` is how the test suite finds a frame without depending on a class
 * string. It is not a style hook.
 */
export function StateFrame({
  stateKey,
  children,
  className,
}: {
  stateKey: string;
  children: ReactNode;
  className?: string | undefined;
}): JSX.Element {
  return (
    <div
      data-state-frame={stateKey}
      className={
        FRAME_BOX +
        (className === undefined ? ' bg-sand border border-border' : ' ' + className)
      }
    >
      {children}
    </div>
  );
}

/**
 * The warning panel, drawn identically in § ৬ (offline banner) and § ৭ (sync banner):
 * `background:#FDF6E8; border-bottom:1px solid #B07A16`.
 *
 * `#FDF6E8` and `#B07A16` are NOT in docs/02's palette. They recur as one pair across
 * seven frames of this file and § ৯ draws `border:1px solid #B07A16` on a white card
 * from the same pair, so they read as two tokens the palette is missing rather than as
 * a slip in one frame. Transcribed as drawn and reported; token-lint flags both, and
 * that flag is correct until docs/02 gains the pair or the design drops it.
 */
export const WARNING_PANEL = 'bg-warning-surface border-b border-warning-border';
/** § ৯'s card border, from the same off-palette pair. */
export const WARNING_BORDER = 'border-warning-border';

/**
 * The 20px back chevron the States frames draw: `M15 5l-7 7 7 7`.
 *
 * `@oprappo/ui`'s `IconButton control="back"` carries the § 01 accessible name
 * `পিছনে যান`, and its glyph is `M14 6l-6 6 6 6` — one pixel from this at the same
 * 24-grid and 2px weight. WP-D reported the same difference from the P0 frames and
 * transcribed the frame's path; this file does the same so the two agree, and the
 * accessible name is carried by hand rather than invented.
 */
export function BackChevron({ stroke = '#0E3A45' }: { stroke?: string }): JSX.Element {
  return (
    <svg width={20} height={20} viewBox="0 0 24 24" fill="none" aria-hidden className="flex-none">
      <path d="M15 5l-7 7 7 7" stroke={stroke} strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

/** The row chevron: `M9 6l6 6-6 6` in `#5B6B69`, drawn at 15px and 16px. */
export function RowChevron({ size, stroke = '#5B6B69' }: { size: number; stroke?: string }): JSX.Element {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" aria-hidden className="flex-none">
      <path d="M9 6l6 6-6 6" stroke={stroke} strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

/**
 * § ৬'s offline glyph: three signal arcs in `#8A5A0C` with a slash across them.
 *
 * `FailureState.Offline` in `@oprappo/ui` draws a two-colour version of the same idea
 * (amber arcs, danger slash, different arc paths). The States frame draws it in one
 * colour with different arcs, so the component is not used here and the difference is
 * reported rather than reconciled — they are two drawings, not one drawing done twice.
 */
export function OfflineGlyph(): JSX.Element {
  return (
    <svg width={20} height={20} viewBox="0 0 24 24" fill="none" aria-hidden className="flex-none mt-[1px]">
      <path
        d="M3 8c5-4 13-4 18 0M6.5 12c3.5-2.5 8-2.5 11 0M10 16c1.5-1 2.5-1 4 0"
        stroke="#8A5A0C"
        strokeWidth={2}
        strokeLinecap="round"
      />
      <path d="M4 20L20 4" stroke="#8A5A0C" strokeWidth={2} strokeLinecap="round" />
    </svg>
  );
}

/** § ৬'s retry glyph: `M20 12a8 8 0 11-2.3-5.7M20 4v4h-4`, 17px, teal. */
export function RetryGlyph(): JSX.Element {
  return (
    <svg width={17} height={17} viewBox="0 0 24 24" fill="none" aria-hidden className="flex-none">
      <path
        d="M20 12a8 8 0 11-2.3-5.7M20 4v4h-4"
        stroke="#0E3A45"
        strokeWidth={2}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

/** § ৯'s tick: `M5 12l5 5 9-10`, 16px, 2.5px, in success. */
export function TickGlyph(): JSX.Element {
  return (
    <svg width={16} height={16} viewBox="0 0 24 24" fill="none" aria-hidden className="flex-none mt-[3px]">
      <path d="M5 12l5 5 9-10" stroke="#1E6B4A" strokeWidth={2.5} strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

/**
 * § ৯'s cross: `M6 6l12 12M18 6L6 18`, 16px, in `#8B9795`.
 * `#8B9795` as an SVG stroke is exactly what docs/02 permits it for.
 */
export function CrossGlyph(): JSX.Element {
  return (
    <svg width={16} height={16} viewBox="0 0 24 24" fill="none" aria-hidden className="flex-none mt-[3px]">
      <path d="M6 6l12 12M18 6L6 18" stroke="#8B9795" strokeWidth={2} strokeLinecap="round" />
    </svg>
  );
}

/**
 * The outlined chip § ৭ hangs on each stale row and § ৮ puts on a closed provider:
 * `border:1px solid #8B9795; font:500 13px/1.75 'Anek Bangla'; color:#5B6B69`.
 * Padding differs by frame — `2px 7px` on the stale row, `3px 8px` on the provider —
 * so it is a prop rather than averaged.
 *
 * § 03's `Badge` has three tones (success / pending / danger) and no outline form, so
 * this is transcribed rather than forced into one. Reported as a `packages/ui` gap.
 */
export function OutlineChip({
  labelBn,
  padding,
}: {
  labelBn: string;
  padding: 'row' | 'provider';
}): JSX.Element {
  return (
    <span
      className={
        'inline-block border border-grey-border font-anek font-medium text-[13px] leading-[1.75] text-muted whitespace-nowrap ' +
        (padding === 'row' ? 'px-[7px] py-[2px]' : 'px-[8px] py-[3px]')
      }
    >
      {labelBn}
    </span>
  );
}

/**
 * The bulleted line § ৪ draws under "যা করতে পারি": a 6px teal disc at `margin-top:7px`
 * and a `400 14.5px/1.75` ink2 line beside it.
 */
export function BulletLine({ children }: { children: ReactNode }): JSX.Element {
  return (
    <div className="flex gap-[11px] items-start">
      <div className="w-[6px] h-[6px] bg-teal rounded-full mt-[7px] flex-none" />
      <div className="flex-1 font-anek font-normal text-[14.5px] leading-[1.75] text-ink2">{children}</div>
    </div>
  );
}

/**
 * The section eyebrow every state frame uses above a list:
 * `font:600 15px/1.75 'Anek Bangla'; color:#5B6B69`. Margins differ per frame and are
 * passed in rather than baked, because § ৫ draws `20px 0 11px` and § ৬ draws
 * `margin-bottom:11px` with no top.
 */
export function SectionLabel({
  children,
  className,
}: {
  children: ReactNode;
  className?: string | undefined;
}): JSX.Element {
  return (
    <div
      className={
        'font-anek font-semibold text-[15px] leading-[1.75] text-muted' +
        (className === undefined ? '' : ' ' + className)
      }
    >
      {children}
    </div>
  );
}

/**
 * The white row with a chevron, drawn in § ৬ (offline list), § ১১ (alternatives) and
 * § ১ (most-used list): `background:#fff;border:1px solid #E2DED5;padding:13px`,
 * title `500 15px/1.75` ink, optional meta `400 13.5px/1.75` muted at `margin-top:1px`.
 */
export function ChevronRow({
  titleBn,
  metaBn,
  onClick,
  className,
}: {
  titleBn: string;
  metaBn?: string | undefined;
  onClick?: (() => void) | undefined;
  className?: string | undefined;
}): JSX.Element {
  return (
    <button
      type="button"
      onClick={onClick}
      className={
        'w-full bg-white border border-border p-[13px] flex justify-between items-center gap-[11px] text-left rounded-none' +
        (className === undefined ? '' : ' ' + className)
      }
    >
      <span className="flex-1">
        <span className="block font-anek font-medium text-[15px] leading-[1.75] text-ink">{titleBn}</span>
        {metaBn === undefined ? null : (
          <span className="block font-anek font-normal text-[13.5px] leading-[1.75] text-muted mt-[1px]">
            {metaBn}
          </span>
        )}
      </span>
      <RowChevron size={15} />
    </button>
  );
}

/**
 * The form the two UNDRAWN states use.
 *
 * `সেবা সাময়িক বন্ধ` and `অনুমোদন অপেক্ষমাণ` are rows 9 and 11 of the applicability
 * matrix and chips in `Architecture § 15 · Required states, every P0 screen`. No frame
 * is drawn for either, anywhere in the design corpus — the eleven drawn frames spend
 * three of their number on variants of `নতুন ব্যবহারকারী` (§ ১ home, § ২ activity,
 * § ৪ AI) and one on `লোডিং`, which leaves these two without a drawing.
 *
 * docs/01 § prohibited: "Invent copy — Every Bangla string in the design is final.
 * Missing strings: ask." So nothing is written for them. This panel carries:
 *
 *   · the state's own matrix row label as its heading — a drawn string
 *   · `message_bn` from the error envelope as its body — server copy, already
 *     localised, which docs/05 § conventions makes the normal source anyway
 *   · nothing else
 *
 * The geometry is § ৯'s card, which is the drawn form for a waiting or blocked panel
 * in this file: `background:#fff; border:1px solid #B07A16; border-left:3px solid
 * #E8A33D; padding:15px`, heading `600 18px/1.75` ink, body `400 14.5px/1.75` ink2 at
 * `margin-top:8px`. Borrowing a drawn geometry is not the same as inventing one, but
 * it IS a decision this package made rather than read, and it is reported as such.
 *
 * W1-04 added the one thing it lacked: a way out. Rule four of `P0 States § চারটি নিয়ম` —
 * "প্রতিটি অবস্থা থেকে বেরোনোর অন্তত একটি পথ থাকবে" — and these two had none. The way out is
 * § ৫'s and § ৯'s white header bar with its back chevron, which needs no new sentence: its
 * name is § 01's `পিছনে যান`, read from `@oprappo/tokens`.
 */
export function UndrawnPanel({
  stateKey,
  headingBn,
  messageBn,
  action,
  onBack,
}: {
  stateKey: string;
  headingBn: string;
  messageBn: string;
  action?: ReactNode | undefined;
  onBack?: (() => void) | undefined;
}): JSX.Element {
  return (
    <StateFrame stateKey={stateKey}>
      <div className="bg-white border-b border-border px-[16px] py-[14px] flex gap-[12px] items-center">
        <button
          type="button"
          aria-label={ariaLabel.back}
          onClick={onBack}
          className="bg-transparent border-0 p-0 rounded-none flex-none"
        >
          <BackChevron />
        </button>
      </div>
      <div className="flex-1 p-[16px] flex flex-col justify-center">
        <div role="status" className={'bg-white border ' + WARNING_BORDER + ' border-l-[3px] border-l-amber p-[15px]'}>
          <div className="font-anek font-semibold text-[18px] leading-[1.75] text-ink">{headingBn}</div>
          <div className="font-anek font-normal text-[14.5px] leading-[1.75] text-ink2 mt-[8px]">{messageBn}</div>
          {action === undefined ? null : <div className="mt-[14px]">{action}</div>}
        </div>
      </div>
    </StateFrame>
  );
}

/**
 * The sand footer strip § ৩, § ৫, § ৯ and § ১১ close with:
 * `background:#F4F0E7;border-top:1px solid #E2DED5;padding:12px 16px`.
 * Text colour differs — § ৩ and § ৯ and § ১১ use body, § ৫ uses muted — so it is a prop.
 */
export function FooterNote({
  children,
  tone = 'body',
  trailing,
}: {
  children: ReactNode;
  tone?: 'body' | 'muted';
  trailing?: ReactNode | undefined;
}): JSX.Element {
  const text = tone === 'body' ? 'text-body' : 'text-muted';
  return (
    <div
      className={
        'bg-sand border-t border-border px-[16px] py-[12px]' +
        (trailing === undefined ? '' : ' flex justify-between items-center gap-[11px]')
      }
    >
      <div className={'font-anek font-normal text-[13px] leading-[1.75] ' + text}>{children}</div>
      {trailing}
    </div>
  );
}
